import type { Invoice, InvoiceStatus, InvoiceType, PaymentMethod } from './types';

export const INVOICE_STATUSES: { value: InvoiceStatus; labelKey: string; color: string }[] = [
  {
    value: 'DRAFT',
    labelKey: 'invoice.status.draft',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  },
  {
    value: 'PENDING',
    labelKey: 'invoice.status.pending',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  {
    value: 'APPROVED',
    labelKey: 'invoice.status.approved',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  {
    value: 'SENT',
    labelKey: 'invoice.status.sent',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  {
    value: 'PAID',
    labelKey: 'invoice.status.paid',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  {
    value: 'PARTIALLY_PAID',
    labelKey: 'invoice.status.partiallyPaid',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  },
  {
    value: 'OVERDUE',
    labelKey: 'invoice.status.overdue',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
  {
    value: 'CANCELLED',
    labelKey: 'invoice.status.cancelled',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  },
  {
    value: 'VOIDED',
    labelKey: 'invoice.status.voided',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  },
];

export const INVOICE_TYPES: { value: InvoiceType; labelKey: string; color: string }[] = [
  {
    value: 'STANDARD',
    labelKey: 'invoice.type.standard',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  {
    value: 'ADVANCE',
    labelKey: 'invoice.type.advance',
    color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  },
  {
    value: 'FINAL',
    labelKey: 'invoice.type.final',
    color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  },
  {
    value: 'PROFORMA',
    labelKey: 'invoice.type.proforma',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  },
  // #925 BE-enum alignment (55Y invoice-list type-enum-fe): a lista/detail-olvasás
  // BE-mapPrismaTypeToType-ja Prisma CREDIT_NOTE→domain STORNO-t ad, + a
  // ListInvoicesQueryDto szűrő-enumja STORNO-t vár (CREDIT_NOTE kikerült onnan,
  // némán STANDARD-ra esne 400 helyett) — ezért a badge+szűrő KÖZÖS forrása itt
  // STORNO-ra vált (a CREDIT_NOTE értéket az InvoiceType típus továbbra is megőrzi,
  // a számla-létrehozás/CreateInvoiceDto külön concern, változatlan).
  {
    value: 'STORNO',
    labelKey: 'invoice.type.storno',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
  {
    value: 'CORRECTION',
    labelKey: 'invoice.type.correction',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  {
    value: 'MINUSZOS',
    labelKey: 'invoice.type.minuszos',
    color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  },
];

// A `invoice.paymentMethod` kulcs MÁR foglalt (mező-címke: "Fizetési mód",
// InvoiceDetailPage/InvoiceFormPage). A `.cash`/`.card`/... nem oldható fel
// alá (string ≠ object ág a JSON-ban) → ezért külön `paymentMethodType` namespace
// az érték-feliratoknak (mintakövetés: `invoice.invoiceType` mező-címke ↔
// `invoice.type.*` érték-objektum).
export const PAYMENT_METHODS: { value: PaymentMethod; labelKey: string }[] = [
  { value: 'CASH', labelKey: 'invoice.paymentMethodType.cash' },
  { value: 'CARD', labelKey: 'invoice.paymentMethodType.card' },
  { value: 'TRANSFER', labelKey: 'invoice.paymentMethodType.transfer' },
  { value: 'COD', labelKey: 'invoice.paymentMethodType.cod' },
];

export const VAT_RATES: { value: string; labelKey: string }[] = [
  { value: '27', labelKey: 'invoice.vatRate.rate27' },
  { value: '18', labelKey: 'invoice.vatRate.rate18' },
  { value: '5', labelKey: 'invoice.vatRate.rate5' },
  { value: '0', labelKey: 'invoice.vatRate.rate0' },
  { value: 'AAM', labelKey: 'invoice.vatRate.aam' },
  { value: 'TAM', labelKey: 'invoice.vatRate.tam' },
  { value: 'EU', labelKey: 'invoice.vatRate.eu' },
  { value: 'EUK', labelKey: 'invoice.vatRate.euk' },
  { value: 'MAA', labelKey: 'invoice.vatRate.maa' },
];

export const NAV_STATUSES: { value: string; labelKey: string; color: string }[] = [
  {
    value: 'PENDING',
    labelKey: 'navStatus.pending',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  {
    value: 'SUBMITTED',
    labelKey: 'navStatus.submitted',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  {
    value: 'ACCEPTED',
    labelKey: 'navStatus.accepted',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  {
    value: 'REJECTED',
    labelKey: 'navStatus.rejected',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
  {
    value: 'ERROR',
    labelKey: 'navStatus.error',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
];

// Dunning level badge config (FIN-FG-4-5)
export const DUNNING_LEVELS: { value: string; labelKey: string; color: string }[] = [
  {
    value: 'NONE',
    labelKey: 'dunning.level.none',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  {
    value: 'REMINDER',
    labelKey: 'dunning.level.reminder',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  {
    value: 'WARNING',
    labelKey: 'dunning.level.warning',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  },
  {
    value: 'FINAL_NOTICE',
    labelKey: 'dunning.level.finalNotice',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
];

// Empty mock data - real data comes from API
export const MOCK_INVOICES: Invoice[] = [];
